import { useState } from 'react'
import type { TemplateDetail } from '@/lib/scientific'
import type { ImplementedCheck } from '@/lib/types'
import { acceptance, RubricScoring } from './TaskRubric'

const description = (check: ImplementedCheck) => {
  const text = (check.scoring_description ?? '').replace(/^Binary:\s*/, '')
  return text ? text[0].toUpperCase() + text.slice(1) : ''
}
const detailsKey = ({ expected: _expected, scoring_description: _description, ...rest }: ImplementedCheck) => JSON.stringify(rest)

export function WorkflowRubric({ data, onSelect }: {
  data: TemplateDetail; onSelect: (id: string) => void
}) {
  const [query, setQuery] = useState('')
  const overview = data.definition.rubric_overview
  const title = (id: string) => data.tasks.find(task => task.task_id === id)?.title ?? id
  if (!overview?.checks.length) return <p className="muted small">Select a task to read its grading criteria.</p>
  const isShared = (criterion: typeof overview.checks[number]) => criterion.variants.length === 1
    && criterion.variants[0].task_ids.length === overview.task_count
  const search = query.trim().toLocaleLowerCase()
  const filtered = overview.checks.filter(criterion => criterion.variants.some(v => `${criterion.id} ${v.check.label} ${description(v.check)} ${acceptance(v.check.expected)}`.toLocaleLowerCase().includes(search)))
  const shared = filtered.filter(isShared)
  const varying = filtered.filter(criterion => !isShared(criterion))
  const taskLinks = (ids: string[]) => <div className="science-rubric-task-links">{ids.map(id => <button type="button" key={id} className="link" onClick={() => onSelect(id)}>{title(id)}</button>)}</div>

  return <div className="science-workflow-rubric">
    <label className="rubric-search"><span className="visually-hidden">Find a workflow check</span><input type="search" placeholder="Find a check or accepted result…" value={query} onChange={e => setQuery(e.target.value)} /></label>
    {search && <p className="muted small" role="status">{filtered.length} of {overview.checks.length} checks match.</p>}
    <p className="muted small">{overview.checks.length} checks across {overview.task_count} {overview.task_count === 1 ? 'task' : 'tasks'}.</p>
    {!!overview.unavailable_task_ids.length && <p className="note">Rubric unavailable for {overview.unavailable_task_ids.map(title).join(', ')}. The comparison is incomplete.</p>}
    {!!shared.length && <section className="science-rubric-section" aria-label="Same for all tasks">
      <h3>Same for all tasks</h3>
      <ul className="science-rubric-list">{shared.map(criterion => {
        const check = criterion.variants[0].check
        const booleanPass = check.expected?.kind === 'equals' && check.expected.value === true
        return <li className="science-rubric-criterion" key={criterion.id}>
          <h4>{check.label}</h4>
          {description(check) && <p>{description(check)}</p>}
          {check.expected && !booleanPass && <p className="science-rubric-answer"><span>Accepted result</span> {acceptance(check.expected)}</p>}
        </li>
      })}</ul>
    </section>}
    {!!varying.length && <section className="science-rubric-section" aria-label="Changes between tasks">
      <h3>Changes between tasks</h3>
      <ul className="science-rubric-list">{varying.map(criterion => {
        const first = criterion.variants[0].check
        const count = criterion.variants.reduce((total, variant) => total + variant.task_ids.length, 0)
        const sameDescription = criterion.variants.every(variant => description(variant.check) === description(first))
        const sameDetails = criterion.variants.every(variant => detailsKey(variant.check) === detailsKey(first))
        return <li className="science-rubric-criterion" key={criterion.id}>
          <h4>{first.label}</h4>
          {sameDescription && description(first) && <p>{description(first)}</p>}
          {count !== overview.task_count && <p className="muted small">Used in {count} of {overview.task_count} tasks.</p>}
          <details className="science-rubric-comparison">
            <summary>Compare task requirements</summary>
            {!sameDetails && <p className="muted small">Grading details also differ. Open a task for its full rule.</p>}
            <div className="science-rubric-table"><table>
              <thead><tr><th scope="col">Task</th><th scope="col">Accepted result{!sameDescription && ' and rule'}</th></tr></thead>
              <tbody>{criterion.variants.map((variant, index) => <tr key={index}>
                <td>{taskLinks(variant.task_ids)}</td>
                <td>{!sameDescription && <p>{description(variant.check)}</p>}
                  <span>{acceptance(variant.check.expected)}</span>
                </td>
              </tr>)}</tbody>
            </table></div>
          </details>
        </li>
      })}</ul>
    </section>}
    <details className="science-rubric-scoring"><summary>How scoring works</summary>
      {overview.scoring.map((group, index) => <div key={index}>
        {overview.scoring.length > 1 && taskLinks(group.task_ids)}
        <RubricScoring aggregation={group.aggregation} />
      </div>)}
      <p className="muted small">Select a task to see its full rubric, including check weights and grading details.</p>
    </details>
  </div>
}
